import { Box, Flex, Grid, SimpleGrid, Spinner, Text } from "@chakra-ui/react";
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const C = {
  primary: "#E85D04",
  secondary: "#9A3412",
  accent: "#C2410C",
  border: "#E8E0D5",
  paper: "#FFFFFF",
  mutedBg: "#F5F0E8",
  muted: "#78716C",
  text: "#292524",
};

const PIE_COLORS = ["#00AA8D", "#9B2C2C"];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      px={3}
      py={2}
      boxShadow="md"
    >
      {label ? (
        <Text fontSize="xs" fontWeight="800" color={C.muted} mb={1}>
          {label}
        </Text>
      ) : null}
      {payload.map((entry) => (
        <Text key={entry.name} fontSize="sm" fontWeight="600" color={C.text}>
          {entry.name}: {entry.value}
          {entry.name?.includes("Accuracy") ? "%" : ""}
        </Text>
      ))}
    </Box>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <Box p={4} bg={C.mutedBg} borderWidth="1px" borderColor={C.border} borderRadius="lg">
      <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase" mb={1}>
        {label}
      </Text>
      <Text fontSize="3xl" fontWeight="800" color={C.primary} lineHeight="1">
        {value}
      </Text>
      {sub ? (
        <Text fontSize="xs" fontWeight="600" color={C.muted} mt={2}>
          {sub}
        </Text>
      ) : null}
    </Box>
  );
}

function ChartCard({ title, children, minH = "240px" }) {
  return (
    <Box
      p={{ base: 4, md: 6 }}
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      w="full"
      minW={0}
    >
      <Text
        fontSize="10px"
        fontWeight="800"
        letterSpacing="0.14em"
        color={C.muted}
        textTransform="uppercase"
        mb={4}
      >
        {title}
      </Text>
      <Box h={minH} w="full" minW={0}>
        {children}
      </Box>
    </Box>
  );
}

const CHART_HEIGHT = "200px";

const HomeAnalyticsCharts = ({ performanceData, isLoading, sessionsSlot = null }) => {
  const stats = useMemo(() => {
    const totalSolved = Number(performanceData?.total_questions_solved) || 0;
    const totalMissed = Number(performanceData?.total_missed_questions) || 0;
    const totalCorrect = Math.max(0, totalSolved - totalMissed);
    const accuracyPct =
      totalSolved > 0 ? ((totalCorrect / totalSolved) * 100).toFixed(1) : "0";
    return { totalSolved, totalMissed, totalCorrect, accuracyPct };
  }, [performanceData]);

  const monthlyData = useMemo(() => {
    return (performanceData?.monthly_accuracy || []).map((item) => {
      const totalQuestions = Number(item.total_questions) || 0;
      const totalRight = Number(item.total_right) || 0;
      const accuracy = Math.min(
        100,
        Math.max(0, parseFloat(String(item.accuracy)) || 0)
      );
      let monthLabel = item.month;
      try {
        monthLabel = new Date(`${item.month}-01`).toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        });
      } catch {
        /* keep raw */
      }
      return {
        month: monthLabel,
        totalQuestions,
        totalRight,
        accuracy: Number(accuracy.toFixed(1)),
      };
    });
  }, [performanceData?.monthly_accuracy]);

  const chapterFocusData = useMemo(() => {
    return [...(performanceData?.most_wrong_chapters || [])]
      .map((ch) => ({
        name: ch.name?.length > 22 ? `${ch.name.slice(0, 20)}…` : ch.name,
        fullName: ch.name,
        correct: Number(ch.right_count) || 0,
        wrong: Number(ch.wrong_count) || 0,
        accuracy: parseFloat(ch.accuracy) || 0,
      }))
      .sort((a, b) => b.wrong - a.wrong || a.accuracy - b.accuracy)
      .slice(0, 6);
  }, [performanceData?.most_wrong_chapters]);

  const pieData = useMemo(
    () => [
      { name: "Correct", value: stats.totalCorrect },
      { name: "Incorrect", value: stats.totalMissed },
    ],
    [stats.totalCorrect, stats.totalMissed]
  );

  const currentMonthLabel = monthlyData.length
    ? monthlyData[monthlyData.length - 1].month
    : "—";
  const currentMonthQuestions = monthlyData.length
    ? monthlyData[monthlyData.length - 1].totalQuestions
    : 0;

  const axisTick = { fill: C.muted, fontSize: 11, fontWeight: 600 };
  const hasData =
    stats.totalSolved > 0 ||
    monthlyData.length > 0 ||
    chapterFocusData.length > 0;

  if (isLoading && !hasData) {
    return (
      <Flex justify="center" py={16}>
        <Spinner size="lg" color={C.primary} thickness="4px" />
      </Flex>
    );
  }

  if (!hasData) {
    return (
      <Box
        p={8}
        bg={C.paper}
        borderWidth="1px"
        borderColor={C.border}
        borderRadius="lg"
        textAlign="center"
      >
        <Text color={C.muted} fontWeight="600">
          Your analytics will appear after you complete practice questions and exams.
        </Text>
      </Box>
    );
  }

  return (
    <Box w="full" minW={0}>
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={6}>
        <StatCard
          label="Questions solved"
          value={stats.totalSolved.toLocaleString()}
          sub="All-time practice volume"
        />
        <StatCard
          label="Overall accuracy"
          value={`${stats.accuracyPct}%`}
          sub={`${stats.totalCorrect} correct · ${stats.totalMissed} incorrect`}
        />
        <StatCard
          label="Latest month"
          value={currentMonthQuestions.toLocaleString()}
          sub={`Questions in ${currentMonthLabel} (main exams only)`}
        />
      </SimpleGrid>

      <Grid
        templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
        templateRows={{ lg: "auto auto" }}
        gap={6}
        w="full"
        minW={0}
        alignItems="stretch"
      >
        <Flex direction="column" gap={6} minW={0}>
          <ChartCard title="Monthly trend & accuracy" minH={CHART_HEIGHT}>
            {monthlyData.length === 0 ? (
              <Flex align="center" justify="center" h="full">
                <Text fontSize="sm" color={C.muted} fontWeight="600">
                  No monthly data yet.
                </Text>
              </Flex>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="month" tick={axisTick} axisLine={{ stroke: C.border }} />
                  <YAxis
                    yAxisId="volume"
                    tick={axisTick}
                    axisLine={{ stroke: C.border }}
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId="accuracy"
                    orientation="right"
                    domain={[0, 100]}
                    tick={axisTick}
                    axisLine={{ stroke: C.border }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: 10, fontWeight: 700, color: C.muted }}
                    iconSize={8}
                  />
                  <Bar
                    yAxisId="volume"
                    dataKey="totalQuestions"
                    name="Questions attempted"
                    fill={C.secondary}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={36}
                  />
                  <Line
                    yAxisId="accuracy"
                    type="monotone"
                    dataKey="accuracy"
                    name="Accuracy %"
                    stroke={C.accent}
                    strokeWidth={2}
                    dot={{ fill: C.accent, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
          {sessionsSlot}
        </Flex>

        <Flex direction="column" gap={6} minW={0}>
          <ChartCard title="Overall performance" minH={CHART_HEIGHT}>
            {stats.totalSolved <= 0 ? (
              <Flex align="center" justify="center" h="full">
                <Text fontSize="sm" color={C.muted} fontWeight="600">
                  No answers recorded yet.
                </Text>
              </Flex>
            ) : (
              <Box position="relative" h="full" w="full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius="58%"
                      outerRadius="78%"
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: 11, fontWeight: 700, color: C.muted }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Flex
                  position="absolute"
                  top="42%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  direction="column"
                  align="center"
                  pointerEvents="none"
                >
                  <Text fontSize="2xl" fontWeight="800" color={C.primary} lineHeight="1">
                    {stats.accuracyPct}%
                  </Text>
                  <Text
                    fontSize="9px"
                    fontWeight="800"
                    color={C.muted}
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                  >
                    Accuracy
                  </Text>
                </Flex>
              </Box>
            )}
          </ChartCard>

          <ChartCard title="Focus areas by chapter" minH={CHART_HEIGHT}>
            {chapterFocusData.length === 0 ? (
              <Flex align="center" justify="center" h="full">
                <Text fontSize="sm" color={C.muted} fontWeight="600">
                  Chapter breakdown will appear as you practice.
                </Text>
              </Flex>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chapterFocusData}
                  layout="vertical"
                  margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                  <XAxis type="number" tick={axisTick} axisLine={{ stroke: C.border }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={88}
                    tick={axisTick}
                    axisLine={{ stroke: C.border }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0]?.payload;
                      return (
                        <Box
                          bg={C.paper}
                          borderWidth="1px"
                          borderColor={C.border}
                          borderRadius="lg"
                          px={3}
                          py={2}
                          boxShadow="md"
                        >
                          <Text fontSize="sm" fontWeight="800" color={C.text} mb={1}>
                            {row?.fullName}
                          </Text>
                          <Text fontSize="xs" color={C.muted}>
                            Correct: {row?.correct} · Wrong: {row?.wrong}
                          </Text>
                          <Text fontSize="xs" color={C.muted}>
                            Accuracy: {row?.accuracy}%
                          </Text>
                        </Box>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, fontWeight: 700, color: C.muted }}
                  />
                  <Bar
                    dataKey="correct"
                    name="Correct"
                    stackId="ch"
                    fill={PIE_COLORS[0]}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="wrong"
                    name="Wrong"
                    stackId="ch"
                    fill={PIE_COLORS[1]}
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Flex>
      </Grid>
    </Box>
  );
};

export default HomeAnalyticsCharts;
